/**
 * Codmon Tabsディレクティブ
 *
 * @description
 * デザインシステムの Tabs コンポーネントに対応するタブ切り替え UI を提供します。
 * 選択中の値は片方向で受け取り、切り替えは on-change で通知します（本体の表示切り替えと
 * 値の更新は呼び出し側で行う）。双方向バインドにすると ng-if などが作る子スコープに
 * 書き戻されて、呼び出し側と値がずれるため。
 *
 * @example
 * // コントローラ側
 * $scope.tabs = [
 *   { value: 'groups', label: 'クラス' },
 *   { value: 'members', label: '園児', testId: 'member' },
 *   { value: 'retired', label: '卒退園児', hidden: !showRetired },
 *   { value: 'archive', label: 'アーカイブ', disabled: true },
 * ];
 * $scope.target = 'groups';
 *
 * $scope.selectTab = function (value) {
 *   $scope.target = value;
 * };
 *
 * // テンプレート側
 * <codmon-tabs items="tabs" selected="target" on-change="selectTab(value)" id-prefix="example"></codmon-tabs>
 * <div ng-show="target == 'groups'" id="example-panel-groups" role="tabpanel" aria-labelledby="example-tab-groups">
 *   ...
 * </div>
 *
 * @property {Array} items - タブ定義の配列。要素は { value, label, disabled?, hidden?, testId? }
 * @property {*} selected - 選択中のタブの value（片方向。本ディレクティブは書き換えない）
 * @property {boolean} [bordered=false] - タブ列の下に区切り線を引くか
 * @property {string} [ariaLabel] - タブ列のアクセシブルネーム
 * @property {string} [idPrefix] - タブとペインを関連付けるIDの接頭辞。
 *   指定するとタブに `{prefix}-tab-{value}` の id と `{prefix}-panel-{value}` の aria-controls が付く。
 *   呼び出し側はペインに同じIDを振り、role="tabpanel" と aria-labelledby を付ける
 * @property {Function} onChange - タブが切り替わったときに呼ばれる。引数 `value` に選択後の value が入る。
 *   選択中の値の更新は呼び出し側で行う
 * @property {boolean} [isNewPrimaryColor=false] - 新プライマリカラー（#017DBC）を使用するか（デフォルト: 旧CoDMON Blue #01A2C1）
 */
angular.module('inspinia').directive('codmonTabs', () => {
  // 矢印キーでの移動に対応するキー
  const NAV_KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

  return {
    restrict: 'E',
    scope: {
      items: '<',
      selected: '<',
      bordered: '<?',
      ariaLabel: '@',
      idPrefix: '@',
      onChange: '&',
      isNewPrimaryColor: '<?',
    },
    templateUrl: `/manage/src/view/directive/codmon-ui/codmon_tabs.html?${new Date().getTime()}`,
    link($scope, element) {
      $scope.tabsClasses = () => ({
        'codmon-tabs--bordered': !!$scope.bordered,
        'codmon-tabs--new-primary-color': !!$scope.isNewPrimaryColor,
      });

      $scope.isActive = (item) => item.value === $scope.selected;

      // 呼び出し側のペインと関連付けるためのID。接頭辞が無いときは属性ごと出さない
      $scope.tabId = (item) => ($scope.idPrefix ? `${$scope.idPrefix}-tab-${item.value}` : undefined);
      $scope.panelId = (item) => ($scope.idPrefix ? `${$scope.idPrefix}-panel-${item.value}` : undefined);

      $scope.tabClasses = (item) => ({
        'codmon-tabs__tab--active': $scope.isActive(item),
      });

      $scope.select = (item) => {
        if (item.disabled || $scope.isActive(item)) return;
        $scope.onChange({ value: item.value });
      };

      /** 画面に出ていて操作できるタブ。DOM 上のボタンと同じ並びになる */
      const navigableItems = () => ($scope.items || []).filter((item) => !item.hidden && !item.disabled);

      /**
       * role="tab" を名乗る以上、Tab キーで入れるのはアクティブなタブだけにして、
       * タブ間の移動は矢印キーに任せる（ARIA Authoring Practices の roving tabindex）。
       * どれもアクティブでないときは先頭だけ Tab で入れるようにして、
       * タブ列に到達できなくなるのを防ぐ
       */
      $scope.tabIndexOf = (item) => {
        if ($scope.isActive(item)) return 0;

        const navigable = navigableItems();
        const hasActive = navigable.some((candidate) => $scope.isActive(candidate));
        if (!hasActive && navigable.length && navigable[0].value === item.value) return 0;

        return -1;
      };

      /** 非表示のタブは DOM に無いので、ボタンの並びは「表示中のタブ」と一致する */
      const focusItem = (item) => {
        const visible = ($scope.items || []).filter((candidate) => !candidate.hidden);
        const index = visible.findIndex((candidate) => candidate.value === item.value);
        const buttons = element[0].querySelectorAll('.codmon-tabs__tab');
        if (index >= 0 && buttons[index]) buttons[index].focus();
      };

      $scope.onKeydown = ($event, item) => {
        if (!NAV_KEYS.includes($event.key)) return;

        const navigable = navigableItems();
        if (!navigable.length) return;
        $event.preventDefault();

        const current = navigable.findIndex((candidate) => candidate.value === item.value);
        let next;
        if ($event.key === 'Home') {
          next = 0;
        } else if ($event.key === 'End') {
          next = navigable.length - 1;
        } else {
          const step = $event.key === 'ArrowRight' ? 1 : -1;
          next = (current + step + navigable.length) % navigable.length;
        }

        const target = navigable[next];
        // フォーカスの移動と同時に切り替える（自動アクティベーション）
        $scope.select(target);
        focusItem(target);
      };
    },
  };
});
